export type SimpleStyleControlOption = { value: string; label: string; description: string; prompt: string };
type Control = { id: string; label: string; group: "foundation" | "limits"; options: SimpleStyleControlOption[] };

const option = (value: string, label: string, description: string, prompt: string): SimpleStyleControlOption => ({ value, label, description, prompt });
export const SIMPLE_STYLE_OFF = option("off", "Off", "Do not use this setting", "");

// One catalog for the editor, persisted values, and generation. Missing values
// mean Off, so existing styles keep exactly their previous prompt behavior.
export const SIMPLE_STYLE_CONTROLS: Control[] = [
  {
    id: "wordChoice",
    label: "Word choice",
    group: "foundation",
    options: [
      option("simple", "Simple", "Everyday, familiar words", "Use everyday, familiar words. Prefer clear, concrete language over ornate or obscure vocabulary."),
      option("expanded", "Expanded", "Wider vocabulary, used naturally", "Use a varied vocabulary naturally. Choose precise words without making the prose feel showy or difficult."),
      option("elevated", "Elevated", "Sophisticated, literary vocabulary", "Use sophisticated, literary vocabulary with precision. Keep meaning clear and avoid strained or gratuitous ornament."),
    ],
  },
  {
    id: "sentenceRhythm",
    label: "Sentence rhythm",
    group: "foundation",
    options: [
      option("short", "Short and punchy", "Short sentences with quick momentum", "Favor short, punchy sentences that create quick momentum. Vary their structure to avoid a monotonous rhythm."),
      option("balanced", "Balanced", "Mix of short and medium-length sentences", "Mix short and medium-length sentences for a balanced rhythm, varying length with the scene's pace."),
      option("flowing", "Flowing", "Longer sentences with a lyrical feel", "Favor longer, flowing sentences with a lyrical cadence, while keeping their structure and meaning clear."),
    ],
  },
  {
    id: "innerThoughts",
    label: "Inner thoughts",
    group: "foundation",
    options: [
      option("surface", "Surface", "No internal monologue", "Keep inner thoughts off the page: convey characters through observable actions and dialogue, without internal monologue."),
      option(
        "moderate",
        "Moderate",
        "Go inside at key decision points",
        "Reveal the viewpoint character's inner thoughts at key decisions and turning points; keep other moments grounded in action and dialogue.",
      ),
      option(
        "deep",
        "Deep",
        "Inside the character's head throughout",
        "Weave the viewpoint character's thoughts, perceptions, and reactions throughout the scene. Respect the story's point of view; do not enter other characters' minds.",
      ),
    ],
  },
  {
    id: "emotionalExpression",
    label: "Emotional expression",
    group: "foundation",
    options: [
      option("subtle", "Subtle", "Emotions are implied via action and dialogue", "Imply emotion through action, dialogue, and subtext rather than directly explaining what characters feel."),
      option("direct", "Direct", "Characters clearly express what they feel", "Make characters' emotions clear through their words and behavior, naming feelings where natural."),
      option(
        "expressive",
        "Expressive",
        "Emotions are vivid and central to the scene",
        "Make emotional expression vivid and central to the scene, using specific actions, dialogue, and reactions rather than generic declarations.",
      ),
    ],
  },
  {
    id: "dialogueBalance",
    label: "Dialogue balance",
    group: "foundation",
    options: [
      option(
        "proseForward",
        "Prose-Forward",
        "More narration and inner thought than dialogue",
        "Favor narration over dialogue. Use inner thought where the point of view and inner-thought setting allow it; reserve dialogue for meaningful exchanges.",
      ),
      option("balanced", "Balanced", "Mix of dialogue and narration", "Balance dialogue with narration, letting both contribute to the scene rather than relying mainly on one."),
      option(
        "dialogueDriven",
        "Dialogue-Driven",
        "Conversation leads the scene",
        "Let conversation lead scenes where characters can interact. Keep narration focused on essential action, context, and dialogue beats.",
      ),
    ],
  },
  {
    id: "descriptiveDensity",
    label: "Descriptive density",
    group: "foundation",
    options: [
      option("minimal", "Minimal", "Only essential description with limited detail", "Keep description minimal: include only details needed to understand the action, setting, or character."),
      option("selective", "Selective", "Add detail where it matters most", "Use description selectively, adding concrete detail at moments that matter to character, atmosphere, or action."),
      option("rich", "Rich", "Layered description with vivid sensory detail", "Use layered description with vivid, specific sensory details, anchored in the viewpoint and relevant to the scene."),
    ],
  },
  {
    id: "intimacy",
    label: "Intimacy",
    group: "limits",
    options: [
      option("sweet", "Sweet", "Attraction and romance, no sex", "Limit intimacy to attraction and romance; do not depict or imply sexual activity."),
      option("closedDoor", "Closed door", "Sex may happen, but stays off the page", "Keep sexual activity entirely off the page. It may be implied, but do not begin or depict it in the scene."),
      option("fadeToBlack", "Fade to black", "Intimacy begins on the page, then cuts away", "Intimacy may begin on the page, but cut away before depicting sexual activity or explicit sexual detail."),
      option(
        "onThePage",
        "On the page",
        "Sex is depicted without graphic detail",
        "If the story calls for sexual intimacy, it may occur on the page without graphic anatomical detail or explicit descriptions of sexual acts.",
      ),
      option(
        "explicit",
        "Explicit",
        "Sex is fully depicted with direct, explicit language",
        "If the story calls for sexual intimacy, explicit depiction and direct language are permitted within the model's content limits. Do not add sexual content solely because this setting permits it.",
      ),
    ],
  },
  {
    id: "violence",
    label: "Violence",
    group: "limits",
    options: [
      option("offPage", "Off the page", "Violence is referenced, but not shown", "Keep violence off the page: it may be referenced, but do not depict the act or graphic injuries."),
      option(
        "restrained",
        "Restrained",
        "Focuses on consequences or aftermath, not the act",
        "Keep violence restrained, focusing on consequences or aftermath rather than depicting the act; avoid graphic injury detail.",
      ),
      option("direct", "Direct", "Violence is depicted plainly, without lingering detail", "When the story calls for violence, depict it plainly and briefly without lingering on injury or gore."),
      option(
        "graphic",
        "Graphic",
        "Violence is detailed and visceral",
        "When the story calls for violence, detailed, visceral depiction is permitted within the model's content limits. Do not add violence solely because this setting permits it.",
      ),
    ],
  },
  {
    id: "profanity",
    label: "Profanity",
    group: "limits",
    options: [
      option("none", "None", "No profanity", "Do not use profanity."),
      option("mild", "Mild", "Occasional mild profanity", "Use at most occasional mild profanity; avoid strong or frequent profanity."),
      option(
        "unrestricted",
        "Unrestricted",
        "Profanity is used naturally without limits",
        "Profanity may be used naturally as appropriate to the characters and scene, without a stylistic restriction on strength or frequency. Do not force it into the prose.",
      ),
    ],
  },
];

export type SimpleStyleControls = Partial<Record<string, string>>;

export function normalizeSimpleStyleControls(value: unknown): SimpleStyleControls {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const input = value as Record<string, unknown>;
  return Object.fromEntries(SIMPLE_STYLE_CONTROLS.flatMap((control) => (control.options.some((option) => option.value === input[control.id]) ? [[control.id, input[control.id] as string]] : [])));
}

export function composeSimpleStyleInstructions(instructions: unknown, controls: unknown): string {
  const text = typeof instructions === "string" ? instructions.trim() : "";
  const normalized = normalizeSimpleStyleControls(controls);
  const sections = (["foundation", "limits"] as const)
    .map((group) => {
      const lines = SIMPLE_STYLE_CONTROLS.filter((control) => control.group === group).flatMap((control) => {
        const selected = control.options.find((option) => option.value === normalized[control.id]);
        return selected ? [`- ${control.label}: ${selected.prompt}`] : [];
      });
      return lines.length ? `${group === "foundation" ? "Foundation" : "Limits"}:\n${lines.join("\n")}` : "";
    })
    .filter(Boolean);
  if (!sections.length) return text;
  // Keep controls ahead of free text so Write's style budget cannot truncate them away.
  return [
    "Apply these selected style controls. Where they conflict with the written style instructions, these controls take precedence. Limits set a ceiling for depiction, not a requirement to include that content.",
    ...sections,
    text,
  ]
    .filter(Boolean)
    .join("\n\n");
}
